import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
export interface GlassStatusCardAction {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: "primary" | "secondary";
}
interface GlassStatusCardProps {
  /** Small uppercase label above the title (e.g. "404", "Application error"). */
  eyebrow?: string;
  title: string;
  description: string;
  actions: GlassStatusCardAction[];
  /** Dev-only error detail (message/stack) — never rendered in production. */
  devDetail?: string;
  /**
   * Render href actions as plain `<a>` (full page load) instead of `Link`.
   * REQUIRED on error boundaries: the error may come from corrupted client/
   * provider state, and a soft client-side navigation carries that state to
   * the destination — a hard navigation rebuilds the app (same recovery
   * semantics as the `window.location.assign()` calls this card replaced).
   * Leave false on healthy-state surfaces like the 404 page.
   */
  hardNavigate?: boolean;
  className?: string;
}

/**
 * SIG-105: shared full-page status card for terminal/boundary screens
 * (not-found, auth/onboarding error boundaries) — glass material per
 * `.claude/rules/frontend-conventions.md`, replacing the pre-design-system
 * `border-border/60 bg-card ... shadow-sm` card that predated the glass
 * material rollout.
 */
export function GlassStatusCard({
  eyebrow,
  title,
  description,
  actions,
  devDetail,
  hardNavigate = false,
  className
}: GlassStatusCardProps) {
  return <div className={cn("flex min-h-screen items-center justify-center bg-background px-4 py-10", className)} data-sentry-component="GlassStatusCard" data-sentry-source-file="glass-status-card.tsx">
      <div className="glass glass-lit w-full max-w-lg rounded-[32px] p-8 text-center depth-effect">
        {eyebrow ? <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-teal-400/80">
            {eyebrow}
          </p> : null}
        <h1 className="mt-2 text-xl font-semibold text-foreground">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>

        {devDetail ? <pre className="mt-4 max-h-40 overflow-auto rounded-lg bg-white/[0.04] p-3 text-left text-xs text-foreground/70">
            {devDetail}
          </pre> : null}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
          {actions.map(action => {
          const variant = action.variant === "secondary" ? "outline" : "accent";
          if (action.href) {
            return <Button key={action.label} asChild variant={variant} size="default">
                  {hardNavigate ? <a href={action.href}>{action.label}</a> : <Link href={action.href}>{action.label}</Link>}
                </Button>;
          }
          return <Button key={action.label} type="button" variant={variant} size="default" onClick={action.onClick}>
                {action.label}
              </Button>;
        })}
        </div>
      </div>
    </div>;
}